import { lang } from '@umu/ukit-lang';
/**
 * @description 音频播放器
 * @author duanjun, wayou
 * @date
 */

import { $document } from '@umu/umu-dom';
import umuWebview from '@umu/umu-webview';
import { interval, isNaN, EventEmitterWrapper } from '@umu/umu-util';
import PlayerManager, { EMediaEvent, IMediaOptions } from './playerManager';

const { isAndroid, isChrome, isIos, isUMUWebview, isWeixinBrowser } = umuWebview.UA_INFO;

interface IAudioPlayerOption extends IMediaOptions {
    autoplay?: boolean;
    preload?: boolean;
    controls?: boolean;
    loop?: boolean;
    file: string; // 播放文件路径
    volume?: number; // 音量改成100分制
    minBufferSize?: number; // 剩下的buffer最少为多少秒
    reloadTimes?: number; // load error 重试次数
    lastPlayedTime?: number; // 跳转的播放点
    container?: string | Element;
}

type PlayerState = 'playing' | 'paused' | 'idle' | 'buffering';

type ErrorEventHandle = (errorEvent: ErrorEvent) => void;
type NormaEventHandle = () => void;

export default class AudioPlayer extends EventEmitterWrapper {
    public bufferLisener = interval(() => {
        // 刚初始化的时候
        this.bufferLisener.bufferStartTime = this.bufferLisener.bufferStartTime || new Date().getTime();
        const nowTime = new Date().getTime();
        const buffered = this.getBuffer();
        this.emit('buffer', buffered);
        if (Math.abs(buffered - this.getDuration()) < 0.5) {
            // buffer至少3s后才中断
            if (nowTime - this.bufferLisener.bufferStartTime > 3000) {
                this.bufferLisener.cancel();
            }
        }
    }, 50);
    public audio: HTMLAudioElement | null = PlayerManager.getPlayer('audio');

    private defaultOptions = {
        autoplay: false,
        preload: true,
        controls: true,
        loop: false,
        file: '', // 播放文件路径
        volume: 100, // 音量改成100分制
        minBufferSize: 1, // 剩下的buffer最少为多少秒
        reloadTimes: 3, // load error 重试次数
        container: 'body',
    };

    private state: PlayerState = 'idle';
    private _options: IAudioPlayerOption;
    private audioEventMap: { [key: string]: ErrorEventHandle | NormaEventHandle } = {};
    private playerId: string = '';
    private container: HTMLElement | null = null;

    constructor(options: IAudioPlayerOption) {
        super();
        this._options = Object.assign({}, this.defaultOptions, options);

        const container = this._options.container;
        if (container && typeof container === 'string') {
            const containerEl = $document.querySelector(container) as HTMLElement;
            if (containerEl) {
                this.container = containerEl;
            }
        }

        this.init();
    }

    public load() {
        PlayerManager.load(this.playerId);
        // if (!this.audio) {
        //     return;
        // }
        // this.audio.load();
        this.bufferLisener();
    }

    public reset() {
        this.bufferLisener.cancel();
    }

    public bindEvent() {
        for (const eventName in this.audioEventMap) {
            if (this.audioEventMap.hasOwnProperty(eventName)) {
                PlayerManager.on(eventName + '-' + this.playerId, this.audioEventMap[eventName]);
            }
        }
    }

    public unbindEvent() {
        for (const eventName in this.audioEventMap) {
            if (this.audioEventMap.hasOwnProperty(eventName)) {
                PlayerManager.off(eventName + '-' + this.playerId, this.audioEventMap[eventName]);
            }
        }
    }

    public reload() {
        const self = this;
        return (() => {
            let times = 0;
            return () => {
                // 1 + 2n
                if (times >= self._options.reloadTimes!) {
                    self.emit('loaderror');
                    return;
                }
                const wait = 100 * (2 * times + 1);
                setTimeout(() => {
                    self.load();
                    times += 1;
                }, wait);
            };
        })();
    }

    public getBuffer(): number {
        if (!this.audio) {
            return 0;
        }
        const bufferEnd = (this.audio.buffered.length && this.audio.buffered.end(this.audio.buffered.length - 1)) || 0;
        return bufferEnd;
    }

    public getState() {
        return this.state;
    }

    public _setState(nextState: PlayerState) {
        const preState = this.getState();
        this.state = nextState;
        this.emit('statechange', nextState, preState);
    }

    public getDuration() {
        if (!this.audio) {
            return 0;
        }

        let duration = this.audio.duration;
        if (isNaN(duration)) {
            duration = 0;
        }
        return duration;
    }

    public getPosition() {
        if (!this.audio) {
            return;
        }

        return this.audio.currentTime;
    }

    public getMute() {
        if (!this.audio) {
            return;
        }

        return this.audio.muted;
    }

    public getVolume() {
        if (!this.audio) {
            return;
        }

        return this.audio.volume * 100;
    }

    public setMute(muted: boolean) {
        if (!this.audio) {
            return;
        }

        this.audio.muted = Boolean(muted);
    }

    // 为了兼容百度播放器采用了100分制
    public setVolume(volume: number) {
        if (!this.audio) {
            return;
        }

        this.audio.volume = volume / 100;
    }

    public play() {
        PlayerManager.play(this.playerId);
        // if (!this.audio) {
        //     return;
        // }

        // this.audio.play();
    }

    public pause() {
        PlayerManager.pause(this.playerId);
        // if (!this.audio) {
        //     return;
        // }

        // this.audio.pause();
    }

    public stop() {
        if (!this.audio) {
            return;
        }

        this.audio.currentTime = 0;
        this.pause();
        this._setState('idle');
    }

    public seek(time: number) {
        time = time;
        if (!this.audio) {
            return;
        }
        const seekable = this.audio.seekable;
        if (seekable.length) {
            time = Math.min(seekable.end(seekable.length - 1), time);
        }
        if (isNaN(time)) {
            time = 0;
        }
        this.audio.currentTime = time;
        this.emit('time', time);
    }

    public setPlaybackRate(rate: number) {
        if (!this.audio) {
            return;
        }
        this.audio.playbackRate = rate;
    }

    public remove() {
        this.reset();
        this.unbindEvent();
        if (this.container && this.audio && this.container.contains(this.audio)) {
            this.container.removeChild(this.audio);
        }
        // @ts-ignore
        delete this.audio;
    }

    public canControlPlaybackRate() {
        // 安卓上的chrome虽然改了值但是没有效果
        if (!isUMUWebview && isAndroid && isChrome) {
            return false;
        }
        let testAudio: HTMLAudioElement | null = $document.createElement('audio');
        const playbackRate = testAudio.playbackRate;
        testAudio.playbackRate = playbackRate / 2 + 0.1;
        setTimeout(() => {
            testAudio = null;
        }, 0);
        return playbackRate !== testAudio.playbackRate;
    }

    public init() {
        this.audioEventMap = {
            [EMediaEvent.playing]: this.playingHandler.bind(this),
            [EMediaEvent.canplay]: this.canPlayHandler.bind(this),
            [EMediaEvent.progress]: this.progressHandler.bind(this),
            [EMediaEvent.timeupdate]: this.timeUpdateHandler.bind(this),
            [EMediaEvent.pause]: this.pauseHandler.bind(this),
            [EMediaEvent.ended]: this.endedHandler.bind(this),
            [EMediaEvent.error]: this.errorHandler.bind(this),
        };

        this.playerId = PlayerManager.add('audio', this._options);
        this.initAudio();
        this.bindEvent();

        if (!this._options.file) {
            console.error(lang('file不能为空!'));
            return;
        }

        if (this._options.preload) {
            this.load();
        }

        if (this._options.autoplay) {
            this.play();
            if (!isIos) {
                return;
            }
            if (isWeixinBrowser) {
                wx.ready(() => {
                    this.play();
                });
            } else {
                const autoPlay = () => {
                    this.play();
                    $document.removeEventListener('touchstart', autoPlay);
                };
                $document.addEventListener('touchstart', autoPlay, false);
            }
        }
    }

    private initAudio() {
        if (this.container && this.audio) {
            this.container.appendChild(this.audio);
        }
    }

    private playingHandler = () => {
        this.emit('playing');
    };

    private progressHandler = () => {
        if (!this.audio) {
            return;
        }
        let buffer = 0;
        if (this.audio.buffered.length) {
            buffer = this.audio.buffered.end(this.audio.buffered.length - 1);
        }

        this.emit('progress', buffer * 1000);
    };

    private canPlayHandler = () => {
        if (!this.audio) {
            return;
        }
        this.emit('canplay');
        // Chrome 中如果音频有缓存，是不会触发 progress 事件的，所以这里手动触发一个，更新界面上的 buffer 条
        this.progressHandler();
    };

    private timeUpdateHandler = () => {
        if (!this.audio) {
            return;
        }
        this.emit('timeupdate', this.audio.currentTime);
    };

    private pauseHandler = () => {
        this.emit('pause');
    };

    private endedHandler = () => {
        this.emit('ended');
    };

    private errorHandler = (errorEvent: ErrorEvent) => {
        this.emit('error', errorEvent);
        this.reload();
        this.reset();
    };
}
